type SteppedTrackParams = {
  minimumValue: number
  maximumValue: number
  step: number
  steps?: number
}

export const resolveSteppedSegmentCount = ({
  minimumValue,
  maximumValue,
  step,
  steps,
}: SteppedTrackParams): number => {
  if (steps != null && steps > 0) {
    return steps
  }

  if (step <= 0) {
    return 1
  }

  return Math.max(1, Math.round((maximumValue - minimumValue) / step))
}

export const resolveSteppedValueStep = ({
  minimumValue,
  maximumValue,
  step,
  steps,
}: SteppedTrackParams): number => {
  const range = maximumValue - minimumValue

  if (range <= 0) {
    return step > 0 ? step : 1
  }

  if (steps != null && steps > 0) {
    return range / steps
  }

  return step > 0 ? step : range
}

export const isSteppedAtMinimum = (
  value: number,
  minimumValue: number
): boolean => {
  return value <= minimumValue
}

export const getSteppedActiveSegmentIndex = (
  value: number,
  params: SteppedTrackParams
): number => {
  const segmentCount = resolveSteppedSegmentCount(params)
  const valueStep = resolveSteppedValueStep(params)

  if (valueStep <= 0) {
    return -1
  }

  if (value <= params.minimumValue) {
    return -1
  }

  if (value >= params.maximumValue) {
    return segmentCount - 1
  }

  const index =
    Math.ceil((value - params.minimumValue) / valueStep) - 1
  return Math.max(0, Math.min(segmentCount - 1, index))
}

export const getSteppedThumbValue = (
  value: number,
  params: SteppedTrackParams
): number => {
  if (value <= params.minimumValue) {
    return params.minimumValue
  }

  const segmentCount = resolveSteppedSegmentCount(params)
  const valueStep = resolveSteppedValueStep(params)
  const activeIndex = getSteppedActiveSegmentIndex(value, params)

  if (activeIndex >= segmentCount - 1) {
    return params.maximumValue
  }

  return params.minimumValue + (activeIndex + 1) * valueStep
}

export const getSteppedValueFromRatio = (
  ratio: number,
  params: SteppedTrackParams
): number => {
  const segmentCount = resolveSteppedSegmentCount(params)
  const valueStep = resolveSteppedValueStep(params)
  const index = Math.min(
    segmentCount - 1,
    Math.max(0, Math.floor(ratio * segmentCount))
  )

  if (index === 0 && ratio < 1 / (2 * segmentCount)) {
    return params.minimumValue
  }

  if (index >= segmentCount - 1) {
    return params.maximumValue
  }

  return params.minimumValue + (index + 1) * valueStep
}

export const getTrackFillPercent = (
  value: number,
  minimumValue: number,
  maximumValue: number
): number => {
  const range = maximumValue - minimumValue || 1
  return ((value - minimumValue) / range) * 100
}
